import React, { useState, useRef, useEffect } from 'react';
import './TransactionPage.css';
import { API_BASE_URL } from '../services/api';


const dayBasisOptions = [364, 365, 360];
const currencyOptions = [
  { id: 'LKR', name: 'LKR' },
  { id: 'USD', name: 'USD' },
  { id: 'EUR', name: 'EUR' },
  { id: 'CAD', name: 'CAD' },
  { id: 'GBP', name: 'GBP' }
]; // Match Transaction tab

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().substring(0, 10);
}

const IsinMasterPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('create');
  
  // Debug state
  const [debugInfo, setDebugInfo] = useState({ status: null, data: null });
  const [form, setForm] = useState({
    isin_issuer: '',
    isin_number: '',
    issue_date: '',
    maturity_date: '',
    coupon_rate: '',
    series: '',
    day_basis: 364,
    currency: 'LKR',
  });
  const [couponDates, setCouponDates] = useState({ coupon_date_1: '', coupon_date_2: '' });
  const [saved, setSaved] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const firstInputRef = useRef(null);
  const [formKey, setFormKey] = useState(0);

  // View ISINs state
  const [isins, setIsins] = useState([]);
  const [loadingIsins, setLoadingIsins] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingIsin, setEditingIsin] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // ISIN dropdown for Create tab
  const [selectedIsinForForm, setSelectedIsinForForm] = useState('');
  const [isinListForDropdown, setIsinListForDropdown] = useState([]);
  const [editForm, setEditForm] = useState({
    isin_issuer: '',
    isin_number: '',
    issue_date: '',
    maturity_date: '',
    coupon_rate: '',
    series: '',
    day_basis: 364,
    currency: 'LKR',
  });
  const [editCouponDates, setEditCouponDates] = useState({ coupon_date_1: '', coupon_date_2: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  // Fetch all ISINs
  const fetchIsins = async () => {
    setLoadingIsins(true);
    try {
      const response = await fetch(`${API_BASE_URL}/isin-master`);
      const data = await response.json();
      if (data.success) {
        setIsins(data.data || []);
        setIsinListForDropdown(data.data || []);
      } else {
        setErrorMessage('Failed to load ISINs');
      }
    } catch (err) {
      setErrorMessage('Error loading ISINs: ' + err.message);
    } finally {
      setLoadingIsins(false);
    }
  };

  // Load ISINs for dropdown when Create tab is selected
  useEffect(() => {
    if (activeTab === 'create' && isinListForDropdown.length === 0) {
      fetchIsins();
    }
  }, [activeTab]);

  // Handle ISIN selection from dropdown to populate form
  const handleIsinSelect = (e) => {
    const selectedIsinNumber = e.target.value;
    setSelectedIsinForForm(selectedIsinNumber);
    
    if (selectedIsinNumber) {
      const selectedIsin = isinListForDropdown.find(isin => isin.isin_number === selectedIsinNumber);
      if (selectedIsin) {
        setForm({
          isin_issuer: selectedIsin.isin_issuer || '',
          isin_number: selectedIsin.isin_number || '',
          issue_date: selectedIsin.issue_date ? selectedIsin.issue_date.split('T')[0] : '',
          maturity_date: selectedIsin.maturity_date ? selectedIsin.maturity_date.split('T')[0] : '',
          coupon_rate: selectedIsin.coupon_rate || '',
          series: selectedIsin.series || '',
          day_basis: selectedIsin.day_basis || 364,
          currency: selectedIsin.currency || 'LKR',
        });
        setCouponDates({
          coupon_date_1: selectedIsin.coupon_date_1 ? selectedIsin.coupon_date_1.split('T')[0] : '',
          coupon_date_2: selectedIsin.coupon_date_2 ? selectedIsin.coupon_date_2.split('T')[0] : '',
        });
      }
    } else {
      // Clear form if no ISIN selected
      setForm({
        isin_issuer: '',
        isin_number: '',
        issue_date: '',
        maturity_date: '',
        coupon_rate: '',
        series: '',
        day_basis: 364,
        currency: 'LKR',
      });
      setCouponDates({ coupon_date_1: '', coupon_date_2: '' });
    }
  };

  // Load ISINs when View tab is selected
  useEffect(() => {
    if (activeTab === 'view') {
      fetchIsins();
    }
  }, [activeTab]);

  const handleChange = (e) => {
    setSaved(false);
    setSuccessMessage('');
    setErrorMessage('');
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'issue_date') {
      setCouponDates({
        coupon_date_1: value,
        coupon_date_2: value ? addMonths(value, 6) : '',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaved(false);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      const payload = {
        ...form,
        coupon_date_1: couponDates.coupon_date_1,
        coupon_date_2: couponDates.coupon_date_2,
      };
      const response = await fetch(`${API_BASE_URL}/isin-master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      let data = null;
      if (response.status !== 204) {
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }
      }
      setDebugInfo({ status: response.status, data });
      console.log('[DEBUG] API response:', response.status, data);
      // Accept 200, 201, 204, or data.success === true as success
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 204 ||
        (typeof data === 'object' && data !== null && (data.success === true || data.status === 'success'))
      ) {
        setForm({
          isin_issuer: '',
          isin_number: '',
          issue_date: '',
          maturity_date: '',
          coupon_rate: '',
          series: '',
          day_basis: 364,
          currency: 'LKR',
        });
        setCouponDates({ coupon_date_1: '', coupon_date_2: '' });
        setSelectedIsinForForm(''); // Clear ISIN dropdown selection
        setSuccessMessage('Successfully saved');
        setErrorMessage('');
        setSaved(true);
        setFormKey(prev => prev + 1); // Force form remount to clear all fields
        // Refresh ISINs list for both tabs
        fetchIsins(); // This will update both the view tab table and the dropdown
        // Focus the first input after a short delay
        setTimeout(() => {
          if (firstInputRef.current) firstInputRef.current.focus();
          setSuccessMessage(''); // Only hide the message, keep Saved state until next edit
        }, 5000);
      } else {
        setSuccessMessage('');
        setErrorMessage(
          data && data.error
            ? (typeof data.error === 'string' ? data.error : JSON.stringify(data.error))
            : 'Failed to save ISIN'
        );
        console.log('[DEBUG] Error from API:', data && data.error);
      }
    } catch (err) {
      setSuccessMessage('');
      setErrorMessage(
        err && err.message
          ? err.message
          : 'An unexpected error occurred'
      );
      setSaved(false); // Always reset button on error
      console.log('[DEBUG] Exception in handleSubmit:', err);
    }
  };

  // Handle view ISIN
  const handleViewIsin = (isin) => {
    setEditingIsin(isin);
    setIsEditMode(false);
    setEditForm({
      isin_issuer: isin.isin_issuer || '',
      isin_number: isin.isin_number || '',
      issue_date: isin.issue_date || '',
      maturity_date: isin.maturity_date || '',
      coupon_rate: isin.coupon_rate || '',
      series: isin.series || '',
      day_basis: isin.day_basis || 364,
      currency: isin.currency || 'LKR',
    });
    setEditCouponDates({
      coupon_date_1: isin.coupon_date_1 || '',
      coupon_date_2: isin.coupon_date_2 || '',
    });
    setViewModalOpen(true);
    setEditError('');
    setEditSuccess('');
  };

  // Handle edit ISIN
  const handleEditIsin = (isin) => {
    setEditingIsin(isin);
    setIsEditMode(true);
    setEditForm({
      isin_issuer: isin.isin_issuer || '',
      isin_number: isin.isin_number || '',
      issue_date: isin.issue_date || '',
      maturity_date: isin.maturity_date || '',
      coupon_rate: isin.coupon_rate || '',
      series: isin.series || '',
      day_basis: isin.day_basis || 364,
      currency: isin.currency || 'LKR',
    });
    setEditCouponDates({
      coupon_date_1: isin.coupon_date_1 || '',
      coupon_date_2: isin.coupon_date_2 || '',
    });
    setViewModalOpen(true);
    setEditError('');
    setEditSuccess('');
  };

  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    if (name === 'issue_date') {
      setEditCouponDates({
        coupon_date_1: value,
        coupon_date_2: value ? addMonths(value, 6) : '',
      });
    }
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    try {
      const payload = {
        ...editForm,
        coupon_date_1: editCouponDates.coupon_date_1,
        coupon_date_2: editCouponDates.coupon_date_2,
      };
      const response = await fetch(`${API_BASE_URL}/isin-master/${editingIsin.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setEditSuccess('ISIN updated successfully');
        fetchIsins();
        setTimeout(() => {
          setViewModalOpen(false);
          setEditSuccess('');
        }, 1500);
      } else {
        setEditError(data.error || 'Failed to update ISIN');
      }
    } catch (err) {
      setEditError('Error updating ISIN: ' + err.message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="page-container ui-page" style={{ width: '100%', maxWidth: '100%' }}>
      <div className="content" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
          <button
            type="button"
            onClick={() => setActiveTab('create')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'create' ? '#1a237e' : 'transparent',
              color: activeTab === 'create' ? '#fff' : '#333',
              cursor: 'pointer',
              borderBottom: activeTab === 'create' ? '3px solid #1a237e' : '3px solid transparent',
              fontWeight: activeTab === 'create' ? 'bold' : 'normal'
            }}
          >
            Create ISIN
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('view')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'view' ? '#1a237e' : 'transparent',
              color: activeTab === 'view' ? '#fff' : '#333',
              cursor: 'pointer',
              borderBottom: activeTab === 'view' ? '3px solid #1a237e' : '3px solid transparent',
              fontWeight: activeTab === 'view' ? 'bold' : 'normal'
            }}
          >
            View ISINs
          </button>
        </div>

        {successMessage && (
          <div style={{ background: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '12px', border: '1px solid #c3e6cb', textAlign: 'center', fontWeight: 500 }}>
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px', borderRadius: '4px', marginBottom: '12px', border: '1px solid #f5c6cb', textAlign: 'center', fontWeight: 500 }}>
            {errorMessage}
          </div>
        )}

        {/* Create ISIN Tab */}
        {activeTab === 'create' && (
          <div className="transaction-form-container" style={{ width: '100%', maxWidth: '100%' }}>
            <h2>ISIN Master - Add ISIN - Fixed Coupon</h2>
            <div className="form-container" style={{ width: '100%' }}>
              {/* ISIN Selection Dropdown */}
              <div className="form-group" style={{ marginBottom: '20px', maxWidth: '500px' }}>
                <label className="form-label" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  Select Existing ISIN (Optional - to use as template)
                </label>
                <select
                  className="form-control"
                  value={selectedIsinForForm}
                  onChange={handleIsinSelect}
                  style={{ padding: '10px' }}
                >
                  <option value="">-- Select an ISIN to use as template --</option>
                  {isinListForDropdown.map(isin => (
                    <option key={isin.isin_number} value={isin.isin_number}>
                      {isin.isin_number} - {isin.isin_issuer || 'Security'}
                    </option>
                  ))}
                </select>
                {selectedIsinForForm && (
                  <div style={{ marginTop: '8px', padding: '8px', background: '#e7f3ff', borderRadius: '4px', fontSize: '13px', color: '#0066cc' }}>
                    ℹ️ Form fields populated from selected ISIN. You can modify them before saving.
                  </div>
                )}
              </div>
              <form key={formKey} onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* ISIN Issuer */}
                  <div className="form-group">
                    <label className="form-label">ISIN Issuer</label>
                    <input ref={firstInputRef} name="isin_issuer" value={form.isin_issuer} onChange={handleChange} className="form-control" required />
                  </div>
                  {/* ISIN Number */}
                  <div className="form-group">
                    <label className="form-label">ISIN Number</label>
                    <input
                      name="isin_number"
                      value={form.isin_number}
                      onChange={handleChange}
                      className="form-control"
                      required
                      autoComplete="off"
                    />
                  </div>
                  {/* Issue Date */}
                  <div className="form-group">
                    <label className="form-label">Issue Date</label>
                    <input type="date" name="issue_date" value={form.issue_date} onChange={handleChange} className="form-control" required />
                  </div>
                  {/* Maturity Date */}
                  <div className="form-group">
                    <label className="form-label">Maturity Date</label>
                    <input type="date" name="maturity_date" value={form.maturity_date} onChange={handleChange} className="form-control" required />
                  </div>
                  {/* Coupon Rate */}
                  <div className="form-group">
                    <label className="form-label">Coupon Rate</label>
                    <input type="number" step="0.0001" name="coupon_rate" value={form.coupon_rate} onChange={handleChange} className="form-control" required />
                  </div>
                  {/* Series */}
                  <div className="form-group">
                    <label className="form-label">Series</label>
                    <input name="series" value={form.series} onChange={handleChange} className="form-control" />
                  </div>
                  {/* Coupon Dates */}
                  <div className="form-group">
                    <label className="form-label">Coupon Dates</label>
                    <input value={couponDates.coupon_date_1} readOnly placeholder="Issue Date" className="form-control" />
                    <input value={couponDates.coupon_date_2} readOnly placeholder="+6 Months" className="form-control" />
                  </div>
                  {/* Day Basis */}
                  <div className="form-group">
                    <label className="form-label">Day Basis</label>
                    <select name="day_basis" value={form.day_basis} onChange={handleChange} className="form-control" required>
                      {dayBasisOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  {/* Currency */}
                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select name="currency" value={form.currency} onChange={handleChange} className="form-control" required>
                      {currencyOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button type="submit" className="submit-btn" disabled={saved}>{saved ? 'Saved' : 'Save'}</button>
              </form>
              {debugInfo.status && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#888' }}>
                  <b>DEBUG:</b> API status: {debugInfo.status}, Data: <pre style={{ display: 'inline', color: '#666' }}>{JSON.stringify(debugInfo.data)}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* View ISINs Tab */}
        {activeTab === 'view' && (
          <div className="transaction-form-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>View ISINs</h2>
              <button
                type="button"
                onClick={fetchIsins}
                style={{
                  padding: '8px 16px',
                  background: '#1a237e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={loadingIsins}
              >
                {loadingIsins ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            {loadingIsins ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>Loading ISINs...</div>
            ) : isins.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>No ISINs found</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
                  <thead style={{ background: '#f1f3f7' }}>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>ID</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>ISIN Number</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>ISIN Issuer</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Issue Date</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Maturity Date</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Coupon Rate</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Series</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Currency</th>
                      <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center', color: '#000', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isins.map(isin => {
                      // Format dates to remove time portion
                      const formatDate = (dateStr) => {
                        if (!dateStr) return 'N/A';
                        // If it's already in YYYY-MM-DD format, return as is
                        if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          return dateStr;
                        }
                        // If it includes time, extract just the date part
                        if (typeof dateStr === 'string' && dateStr.includes('T')) {
                          return dateStr.split('T')[0];
                        }
                        // Try to parse as Date and format
                        try {
                          const date = new Date(dateStr);
                          if (!isNaN(date.getTime())) {
                            return date.toISOString().split('T')[0];
                          }
                        } catch (e) {
                          return dateStr;
                        }
                        return dateStr;
                      };
                      
                      return (
                        <tr key={isin.id}>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{isin.id}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{isin.isin_number}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{isin.isin_issuer}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{formatDate(isin.issue_date)}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{formatDate(isin.maturity_date)}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{isin.coupon_rate}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{isin.series || 'N/A'}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', color: '#000' }}>{isin.currency}</td>
                          <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                            <button
                              onClick={() => handleViewIsin(isin)}
                              style={{
                                padding: '6px 12px',
                                background: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                marginRight: '5px'
                              }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEditIsin(isin)}
                              style={{
                                padding: '6px 12px',
                                background: '#28a745',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* View/Edit Modal */}
        {viewModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={() => setViewModalOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>{isEditMode ? 'Edit ISIN' : 'View ISIN'}</h2>
                <button
                  onClick={() => setViewModalOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {editError && (
                <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px', borderRadius: '4px', marginBottom: '12px' }}>
                  {editError}
                </div>
              )}
              {editSuccess && (
                <div style={{ background: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '12px' }}>
                  {editSuccess}
                </div>
              )}

              {isEditMode ? (
                <form onSubmit={handleEditSubmit}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">ISIN Issuer</label>
                      <input
                        name="isin_issuer"
                        value={editForm.isin_issuer}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ISIN Number</label>
                      <input
                        name="isin_number"
                        value={editForm.isin_number}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Issue Date</label>
                      <input
                        type="date"
                        name="issue_date"
                        value={editForm.issue_date}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Maturity Date</label>
                      <input
                        type="date"
                        name="maturity_date"
                        value={editForm.maturity_date}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coupon Rate</label>
                      <input
                        type="number"
                        step="0.0001"
                        name="coupon_rate"
                        value={editForm.coupon_rate}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Series</label>
                      <input
                        name="series"
                        value={editForm.series}
                        onChange={handleEditFormChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coupon Dates</label>
                      <input value={editCouponDates.coupon_date_1} readOnly placeholder="Issue Date" className="form-control" />
                      <input value={editCouponDates.coupon_date_2} readOnly placeholder="+6 Months" className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Day Basis</label>
                      <select
                        name="day_basis"
                        value={editForm.day_basis}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      >
                        {dayBasisOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <select
                        name="currency"
                        value={editForm.currency}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      >
                        {currencyOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button
                      type="button"
                      onClick={() => setViewModalOpen(false)}
                      style={{
                        padding: '10px 20px',
                        background: '#6c757d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={editLoading}
                    >
                      {editLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">ISIN Issuer</label>
                      <input value={editForm.isin_issuer} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ISIN Number</label>
                      <input value={editForm.isin_number} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Issue Date</label>
                      <input value={editForm.issue_date} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Maturity Date</label>
                      <input value={editForm.maturity_date} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coupon Rate</label>
                      <input value={editForm.coupon_rate} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Series</label>
                      <input value={editForm.series || 'N/A'} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coupon Dates</label>
                      <input value={editCouponDates.coupon_date_1} readOnly className="form-control" />
                      <input value={editCouponDates.coupon_date_2} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Day Basis</label>
                      <input value={editForm.day_basis} readOnly className="form-control" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <input value={editForm.currency} readOnly className="form-control" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditMode(true);
                        setEditError('');
                        setEditSuccess('');
                      }}
                      style={{
                        padding: '10px 20px',
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewModalOpen(false)}
                      style={{
                        padding: '10px 20px',
                        background: '#6c757d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IsinMasterPage;
